import React, { createContext, useContext, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { componentColors } from './components';
import type { ThemeName } from './tokens';

// Create context for theme values
const ThemeContext = createContext<ThemeName>('light');

// CSS variable helper
const toCssVar = (key: string, value: string) => {
  // Convert camelCase to kebab-case
  const cssKey = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  return `--${cssKey}: ${value};`;
};

// Convert theme object to CSS variables
const themeToCSS = (theme: Record<string, any>, prefix = ''): string[] => {
  return Object.entries(theme).flatMap(([key, value]) => {
    const fullKey = prefix ? `${prefix}-${key}` : key;
    if (typeof value === 'object') {
      return themeToCSS(value, fullKey);
    }
    return [toCssVar(fullKey, value)];
  });
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { currentTheme } = useSettings();
  const theme = currentTheme as ThemeName;

  // Apply theme CSS variables
  useEffect(() => {
    const themeColors = componentColors[theme];
    const cssVars = themeToCSS(themeColors);

    // Create style element
    const style = document.createElement('style');
    style.setAttribute('id', 'theme-vars');
    style.textContent = `
      .theme-root { ${cssVars.join(' ')} }
      .theme-root * { color-scheme: ${theme}; }
    `;

    // Replace existing style or add new one
    const existingStyle = document.getElementById('theme-vars');
    if (existingStyle) {
      existingStyle.replaceWith(style);
    } else {
      document.head.appendChild(style);
    }

    // Apply dark mode class
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    return () => {
      style.remove();
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      <div className="theme-root">{children}</div>
    </ThemeContext.Provider>
  );
}

// Hook to access current theme name
export function useTheme() {
  return useContext(ThemeContext);
}

// Hook to get component colors
export function useComponentColor<K extends keyof typeof componentColors.light>(component: K) {
  const theme = useTheme();
  return componentColors[theme][component];
}

// Hook to get a specific color variable
export function useThemeVar(path: string) {
  return `var(--${path.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()})`;
}
